import type { FichaProyectoPyg } from './tipos'
import estilos from './PygProyectos.module.css'

function formatoPesos(valor: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Math.round(valor))
}

type Props = {
  fichas: FichaProyectoPyg[]
  onSeleccionarProyecto: (id: string) => void
}

export function CruceProyectosRentabilidad({ fichas, onSeleccionarProyecto }: Props) {
  if (fichas.length === 0) return null

  return (
    <section className={estilos.seccionProyectos}>
      <div className={estilos.cabeceraGrafico}>
        <div className={estilos.tituloGrafico}>
          <h3>Rentabilidad y Margen por Proyecto (Cruce Directo)</h3>
          <span className={estilos.subtituloGrafico}>
            Lo producido contra sus costos incurridos de mano de obra y materiales para cada proyecto.
          </span>
        </div>
      </div>

      <div className={estilos.gridFichasProyectos}>
        {fichas.map((proy) => {
          const alDia = proy.producidoTotal >= proy.planeadoAHoy
          const margenPositivo = proy.margenTotal >= 0
          const pctMeta = proy.presupuesto > 0
            ? Math.min(100, Math.round((proy.planeadoAHoy / proy.presupuesto) * 100))
            : 0

          return (
            <div
              key={proy.id}
              className={estilos.fichaProyCard}
              onClick={() => onSeleccionarProyecto(proy.id)}
              style={{ cursor: 'pointer' }}
              title="Click para ver P&G detallado de este proyecto"
            >
              <div className={estilos.cabeceraFicha}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span className={estilos.badgeProyCodigo}>{proy.codigo}</span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {proy.estado.replace('_', ' ')}
                    </span>
                  </div>
                  <h4 className={estilos.nombreProyFicha}>{proy.nombre}</h4>
                  {proy.cliente && (
                    <span className={estilos.clienteProyFicha}>Cliente: {proy.cliente}</span>
                  )}
                </div>
              </div>

              {/* Grid de métricas de la ficha */}
              <div className={estilos.metricasGridFicha}>
                <div className={estilos.metricaItem}>
                  <span className={estilos.metricaLabel}>Presupuesto</span>
                  <span className={estilos.metricaValor}>{formatoPesos(proy.presupuesto)}</span>
                </div>
                <div className={estilos.metricaItem}>
                  <span className={estilos.metricaLabel}>Producido a la fecha</span>
                  <span className={estilos.metricaValor} style={{ color: '#0aa3a8' }}>
                    {formatoPesos(proy.producidoTotal)}
                  </span>
                </div>
                <div className={estilos.metricaItem}>
                  <span className={estilos.metricaLabel}>Costo Incurrido</span>
                  <span className={estilos.metricaValor} style={{ color: '#c2334d' }}>
                    {formatoPesos(proy.costoTotal)}
                  </span>
                </div>
                <div className={estilos.metricaItem}>
                  <span className={estilos.metricaLabel}>Margen Directo</span>
                  <span
                    className={estilos.metricaValor}
                    style={{ color: margenPositivo ? '#166534' : '#c2334d' }}
                  >
                    {formatoPesos(proy.margenTotal)} ({proy.margenPct}%)
                  </span>
                </div>
              </div>

              {/* Barra de avance con meta esperada */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: 4 }}>
                  <span>Avance físico: <b>{proy.avanceGlobalPct}%</b></span>
                  <span style={{ color: '#f59e0b' }}>Meta esperada: <b>{pctMeta}%</b></span>
                </div>
                <div className={estilos.barraProgresoFicha}>
                  <div
                    className={estilos.barraProgresoFill}
                    style={{ width: `${Math.min(100, proy.avanceGlobalPct)}%` }}
                  />
                  <div
                    className={estilos.barraProgresoMeta}
                    style={{ left: `${pctMeta}%` }}
                    title={`Meta programada a hoy: ${pctMeta}%`}
                  />
                </div>
              </div>

              {/* Pie con indicador de desviación */}
              <div className={estilos.pieFicha}>
                <span>
                  {alDia ? (
                    <span className={estilos.badgeOk}>Al día con el plan</span>
                  ) : (
                    <span className={estilos.badgeAlerta}>
                      {formatoPesos(proy.planeadoAHoy - proy.producidoTotal)} rezagado
                    </span>
                  )}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {proy.fecha_inicio.slice(0, 10)} → {proy.fecha_fin.slice(0, 10)}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
